import React, { useState } from "react";

import DOMPurify from "dompurify";

import cx from "classnames";
import styles from "./readMore.module.scss";
import { useIsOverflow } from "../hooks/useIsOverflow";

interface ReadMoreProps {
  id: string;
  text: string;
  amountOfCharacters?: number;
  toggleExpand?: boolean;
}

export const ReadMore = ({ id, text, amountOfCharacters = 150, toggleExpand = true }: ReadMoreProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const splittedText = text.split("");

  const canOverflow = splittedText.length > amountOfCharacters;
  const beginText = canOverflow
    ? splittedText.slice(0, amountOfCharacters).join("")
    : text;

  let endText = splittedText.slice(amountOfCharacters).join("");

  const lines: string[] = [];
  for (let i = 0; i < endText.length; i += amountOfCharacters) {
    lines.push(endText.slice(i, i + amountOfCharacters));
  }

  endText = lines.join("<br/>");

  return (
    <>
      <span
        id={id}
        className={styles.content}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(beginText, { ADD_ATTR: ['target', 'rel'] }),
        }}
      />
      {canOverflow && (
        <>
          {toggleExpand && !isExpanded && <span>... </span>}
          <span
            className={cx(styles.readMoreContent, {
              [styles.hidden]: toggleExpand && !isExpanded,
            })}
            aria-hidden={!isExpanded}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(endText, { ADD_ATTR: ['target', 'rel'] }),
            }}
          ></span>
          {toggleExpand && (<span
            className={styles.readMore}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            aria-controls={id}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "read less" : "read more"}
          </span>)}
        </>
      )}
    </>
  );
};

interface ReadMoreHtmlProps {
  className?: string;
  style?: any;
  children: React.ReactNode;
}

export const ReadMoreHtml: React.FC<ReadMoreHtmlProps> = ({ className, style, children }) => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(elementRef);

  const [showMore, setShowMore] = React.useState(false);

  const handleClick = (e) => {
    if(showMore) {
      elementRef.current!.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }else{
      const height = elementRef.current!.clientHeight / 2;
      elementRef.current!.scrollTo({
        top: height,
        behavior: 'smooth'
      });
    }

    setShowMore(!showMore);
  }

  return (
    <div className={cx(styles.container, className, {[styles.disabled]: !showMore})} style={style}>
      <div ref={elementRef} className={cx(styles.content, {[styles.overflow]: isOverflow})}>{children}</div>
      {isOverflow && 
        <div className={styles.readMore}>
          <span className={styles.readMore} onClick={handleClick}>{showMore ? 'show less' : 'read more'}</span>
        </div>
      }
    </div>
  )
}